export const SCRIPT_ROLE_SCORE_KEYS = [
  "roleCategory",
  "story",
  "tags",
  "tone",
  "intensity",
  "riskPenalty",
] as const;

export type ScriptRoleScoreKey = (typeof SCRIPT_ROLE_SCORE_KEYS)[number];

export interface ScriptRoleScoreRubricItem {
  key: ScriptRoleScoreKey;
  label: string;
  maxScore: number;
  description: string;
}

/** 评分量表唯一真相源 */
export const SCRIPT_ROLE_SCORE_RUBRIC: ScriptRoleScoreRubricItem[] = [
  {
    key: "roleCategory",
    label: "角色分类匹配",
    maxScore: 25,
    description: "奶奶辈/宝妈/小年轻等，与账号人设类目是否一致",
  },
  {
    key: "story",
    label: "具体故事匹配",
    maxScore: 30,
    description: "老伴、儿子、湿气重、三高、复购等具体经历是否一致",
  },
  {
    key: "tags",
    label: "产品/健康/家庭标签匹配",
    maxScore: 20,
    description: "产品、健康点、家庭关系标签是否一致",
  },
  {
    key: "tone",
    label: "语气匹配",
    maxScore: 10,
    description: "临时角色全天话术语气是否像该账号",
  },
  {
    key: "intensity",
    label: "出场强度匹配",
    maxScore: 5,
    description: "话多/话少，是否适合主线或辅助账号",
  },
  {
    key: "riskPenalty",
    label: "风险/冲突扣分",
    maxScore: -30,
    description:
      "具体经历冲突、产品冲突、身份冲突（台词说话人视角与账号人设类目不符）、禁忌话题命中",
  },
];

export const SCRIPT_ROLE_SCORE_RUBRIC_MAP = Object.fromEntries(
  SCRIPT_ROLE_SCORE_RUBRIC.map((item) => [item.key, item])
) as Record<ScriptRoleScoreKey, ScriptRoleScoreRubricItem>;

export const SCRIPT_ROLE_POSITIVE_MAX_SCORE = SCRIPT_ROLE_SCORE_RUBRIC.filter(
  (item) => item.maxScore > 0
).reduce((sum, item) => sum + item.maxScore, 0);

export interface RoleScoreDimensionInput {
  key: ScriptRoleScoreKey;
  score: number;
  reason: string;
}

export interface RoleScoreDimension extends RoleScoreDimensionInput {
  label: string;
  maxScore: number;
}

export function buildMatchingRubricPromptText() {
  return SCRIPT_ROLE_SCORE_RUBRIC.map((item) => {
    const range =
      item.key === "riskPenalty"
        ? "分值范围 -30 到 0（0 表示无冲突，负分表示扣分）"
        : `分值范围 0 到 ${item.maxScore}`;
    return `- ${item.label}（${item.key}，满分 ${item.maxScore}）：${item.description}；${range}`;
  }).join("\n");
}

export function computeTotalScore(breakdown: Array<{ score: number }>) {
  const raw = breakdown.reduce((sum, item) => sum + item.score, 0);
  return Math.max(0, Math.min(100, raw));
}

export function normalizeScoreBreakdown(
  input: RoleScoreDimensionInput[]
): RoleScoreDimension[] {
  validateScoreBreakdown(input);

  const inputMap = new Map(input.map((item) => [item.key, item]));
  return SCRIPT_ROLE_SCORE_RUBRIC.map((rubric) => {
    const item = inputMap.get(rubric.key)!;
    return {
      key: rubric.key,
      label: rubric.label,
      maxScore: rubric.maxScore,
      score: item.score,
      reason: item.reason.trim(),
    };
  });
}

export function validateScoreBreakdown(input: RoleScoreDimensionInput[]) {
  if (input.length !== SCRIPT_ROLE_SCORE_KEYS.length) {
    throw new Error(
      `评分明细必须包含 ${SCRIPT_ROLE_SCORE_KEYS.length} 个维度，实际 ${input.length} 个`
    );
  }

  const keys = new Set(input.map((item) => item.key));
  for (const key of SCRIPT_ROLE_SCORE_KEYS) {
    if (!keys.has(key)) {
      throw new Error(`评分明细缺少维度：${key}`);
    }
  }

  if (keys.size !== SCRIPT_ROLE_SCORE_KEYS.length) {
    throw new Error("评分明细包含重复或未知维度");
  }

  for (const item of input) {
    const rubric = SCRIPT_ROLE_SCORE_RUBRIC_MAP[item.key];
    if (!item.reason?.trim()) {
      throw new Error(`维度 ${item.key} 缺少评分说明`);
    }

    if (item.key === "riskPenalty") {
      if (item.score > 0 || item.score < -30) {
        throw new Error(`维度 riskPenalty 分值必须在 -30 到 0 之间，实际 ${item.score}`);
      }
      continue;
    }

    if (item.score < 0 || item.score > rubric.maxScore) {
      throw new Error(
        `维度 ${item.key} 分值必须在 0 到 ${rubric.maxScore} 之间，实际 ${item.score}`
      );
    }
  }
}
